
import {
  computed, defineComponent, onMounted, ref,
} from 'vue';
import { useI18n } from 'vue-i18n';
import moment from 'moment';
import { useStore } from '@/store';
import { UserState } from '@/composables/User';
import ProgressSpinner from 'primevue/progressspinner';

import PlanHeroCard from '@/components/billing/panels/PlanHeroCard.vue';
import LegacyPlanHeroCard from '@/components/billing/panels/LegacyPlanHeroCard.vue';
import AlertBanner from '@/components/billing/panels/AlertBanner.vue';
import WalletPanel from '@/components/billing/panels/WalletPanel.vue';
import WalletLegacyPanel from '@/components/billing/panels/WalletLegacyPanel.vue';
import PaymentMethodsPanel from '@/components/billing/panels/PaymentMethodsPanel.vue';
import BillsList from '@/components/billing/panels/BillsList.vue';
import MigrationCallout from '@/components/billing/panels/MigrationCallout.vue';
import BillingAutoExclusionPanel from '@/components/billing/panels/BillingAutoExclusionPanel.vue';
import Dialog from 'primevue/dialog';

import UnsubscribeDialog from '@/components/billing/dialogs/UnsubscribeDialog.vue';
import ChangePlanDialog from '@/components/billing/dialogs/ChangePlanDialog.vue';
import MigrateV5Dialog from '@/components/billing/dialogs/MigrateV5Dialog.vue';
import AddPaymentMethodDialog from '@/components/billing/dialogs/AddPaymentMethodDialog.vue';
import BuyCreditsDialog from '@/components/billing/dialogs/BuyCreditsDialog.vue';
import RepayInvoiceDialog from '@/components/billing/dialogs/RepayInvoiceDialog.vue';
import LegacyBillingDialog from '@/components/billing/dialogs/LegacyBillingDialog.vue';
import BillingConfirmDialog from '@/components/billing/shared/BillingConfirmDialog.vue';

import {
  bumpShopifyCappedAmount,
  deletePaymentMethod,
  downloadInvoicePdf,
  extendTrial,
  setDefaultPaymentMethod,
  undoCancelPlan,
  undoDowngradeNextPlan,
} from '@/composables/billing/billingApi';
import { useStructuralReload } from '@/composables/billing/useStructuralReload';
import { usePendingPaymentPolling } from '@/composables/billing/usePendingPaymentPolling';
import { showToastError } from '@/helpers';
import { BillingAlert, InvoiceListItem } from '@/types/billing-types';

export default defineComponent({
  name: 'SubscriptionPage',

  components: {
    ProgressSpinner,
    PlanHeroCard,
    LegacyPlanHeroCard,
    AlertBanner,
    WalletPanel,
    WalletLegacyPanel,
    PaymentMethodsPanel,
    BillsList,
    MigrationCallout,
    BillingAutoExclusionPanel,
    Dialog,
    UnsubscribeDialog,
    ChangePlanDialog,
    MigrateV5Dialog,
    AddPaymentMethodDialog,
    BuyCreditsDialog,
    RepayInvoiceDialog,
    LegacyBillingDialog,
    BillingConfirmDialog,
  },

  setup() {
    const { t } = useI18n();
    const store = useStore();
    const handleReload = useStructuralReload();
    usePendingPaymentPolling();

    const shopOffer = computed(() => store.state.billing.shopOffer);
    const overview = computed(() => store.state.billing.overview);
    const loading = computed(
      () => store.state.billing.loadingOverview
        || store.state.billing.loadingOffer
        || !shopOffer.value,
    );

    const currencySymbol = computed(() => (
      overview.value?.shop?.currency === 'USD' ? '$' : '€'
    ));

    // Shopify shops manage their invoices in the Shopify back-office — the
    // BillsList shows a notice instead, and we skip the /billing/invoices call.
    const isShopify = computed(
      () => overview.value?.shop?.solutionEcommerce === 'shopify',
    );

    // Modal « Exclure automatiquement des contacts de la facturation » (V5),
    // ouverte depuis l'action du hero (PlanHeroCard, affichée si métrique=contacts).
    const autoExclusionDialogVisible = ref(false);
    const openAutoExclusionDialog = () => {
      autoExclusionDialogVisible.value = true;
    };

    // True when the shop's only "plan" is a trial — used to neutralize the
    // `currentPlanCode` passed to the ChangePlanDialog so the comparator
    // doesn't mistake the trial plan for an actual subscription (no
    // "OFFRE ACTUELLE" pill, no upgrade/downgrade verbiage on the CTAs).
    const isOnTrial = computed(() => {
      const status = overview.value?.plan?.status;
      return status === 'trial' || status === 'trial_expiring_soon';
    });

    /**
     * Effective plan discount percentage to surface in the ChangePlanDialog
     * comparator. We prefer the backend-computed `overview.plan.discountPercent`
     * when present (it already encodes the date check), and otherwise derive it
     * from the raw `shops_plans.plan` JSON (`UserState.planData`) :
     *   - `discount.plan`        → the `−%` on the plan price,
     *   - `discountLimit.plan`   → the discount end date (YYYY-MM-DD).
     * The discount is only applied when active (> 0) AND its end date is today
     * or later (a missing limit is treated as no expiry). Display-only — the
     * actually-charged amount is computed server-side in the payment steps.
     */
    const planDiscountPercent = computed<number | null>(() => {
      const fromOverview = overview.value?.plan?.discountPercent;
      if (fromOverview != null && fromOverview > 0) return fromOverview;

      const pct = Number(UserState.planData?.discount?.plan ?? 0);
      if (!Number.isFinite(pct) || pct <= 0) return null;

      const limit = UserState.planData?.discountLimit?.plan;
      if (limit && moment(limit, 'YYYY-MM-DD').isBefore(moment(), 'day')) {
        return null;
      }
      return pct;
    });

    // Hide alerts that are already conveyed by the hero card. When there is
    // no active plan, the hero already shows a big "Choisir une offre" CTA —
    // an extra info banner with the same CTA above it looks duplicated. We
    // only skip the alert in that specific case (no plan + same CTA action),
    // so genuine alerts like "your plan expires soon" still render.
    const visibleAlertsCount = computed(() => {
      const alerts = overview.value?.alerts ?? [];
      // No active offer: modern shops have no plan.code, V3 shops have a
      // legacyPlan with status 'none'. In both cases the hero already shows a
      // big "choose offer" CTA, so the duplicate banner is filtered out.
      const hasNoPlan = !overview.value?.plan?.code
        && overview.value?.legacyPlan?.status !== 'active';
      return alerts.filter((a: BillingAlert) => {
        if (hasNoPlan && a?.cta?.action === 'open-change-plan') return false;
        return true;
      }).length;
    });

    // Dialogs
    const unsubscribeDialogVisible = ref(false);
    const changePlanDialogVisible = ref(false);
    const migrateV5DialogVisible = ref(false);
    const addPaymentMethodDialogVisible = ref(false);
    const buyCreditsDialogVisible = ref(false);
    const repayInvoiceDialogVisible = ref(false);
    const invoiceBeingRepaid = ref<InvoiceListItem | null>(null);
    const pendingTrialExtendAfterCard = ref(false);
    const legacyBillingDialogVisible = ref(false);

    // Invoices — the table is autonomous (SpmTableAPI). We only keep a ref
    // here to trigger a refresh after a successful repay.
    const billsListRef = ref<{ reloadData: () => Promise<void> } | null>(null);

    const refreshOverview = async () => {
      await store.dispatch('billing/fetchShopOffer');
      await store.dispatch('billing/refetchOverview');
    };

    /**
     * Refreshes the overview + first invoices page after a successful credit
     * purchase. The credit row is inserted by the Adyen AUTHORISATION webhook
     * which is asynchronous — typically <1s via ngrok in test, a few seconds
     * in prod. A single immediate refetch would race the webhook and show
     * stale data, so we poll a few times with increasing delays and stop as
     * soon as the wallet's `purchased` balance has changed (or after the
     * total budget of ~15s).
     */
    const onCreditsBought = async () => {
      const initialPurchased = overview.value?.wallet?.balance?.breakdown?.purchased?.amount ?? 0;
      const delays = [800, 1500, 2500, 4000, 6000];
      for (const delay of delays) {
        await new Promise((r) => { setTimeout(r, delay); });
        await Promise.all([
          refreshOverview(),
          isShopify.value ? Promise.resolve() : (billsListRef.value?.reloadData() ?? Promise.resolve()),
        ]);
        const newPurchased = overview.value?.wallet?.balance?.breakdown?.purchased?.amount ?? 0;
        if (newPurchased > initialPurchased) return;
      }
    };

    /**
     * Refresh after a successful plan subscription / change. Symmetric to
     * {@link onCreditsBought} : the plan row is written by the Adyen
     * AUTHORISATION webhook, which is asynchronous (a few seconds), so a single
     * immediate refetch races it and the hero card stays on « Aucun plan actif »
     * until a manual F5. We poll the overview until the active plan appears /
     * changes, then reload the page — a subscription is a structural action
     * (the backend returns `requiresAuthRefresh`) so the shop's rights /
     * capabilities must be refreshed app-wide, exactly like the saved-card path
     * already did via `useStructuralReload`.
     */
    const onPlanSubscribed = async () => {
      const previousCode = overview.value?.plan?.code ?? null;
      const delays = [800, 1500, 2500, 4000, 6000];
      for (const delay of delays) {
        await new Promise((r) => { setTimeout(r, delay); });
        await refreshOverview();
        const code = overview.value?.plan?.code ?? null;
        if (code && code !== previousCode) break;
      }
      // Reload AFTER the webhook has applied the plan (guaranteed by the poll
      // above) so the freshly unlocked plan rights/capabilities take effect
      // app-wide — reloading before would land back on the stale state.
      window.location.reload();
    };

    // Action handlers
    const openChangePlanDialog = () => {
      // V3 shops still use the legacy Zend iframe for plan changes — the
      // modern ChangePlanDialog doesn't know how to handle V3 pricing.
      if (overview.value?.capabilities?.isLegacy) {
        legacyBillingDialogVisible.value = true;
        return;
      }
      changePlanDialogVisible.value = true;
    };
    const openMigrateDialog = () => { migrateV5DialogVisible.value = true; };
    const openUnsubscribeDialog = () => { unsubscribeDialogVisible.value = true; };
    const openBuyCreditsDialog = () => {
      // V3 shops buy credits through the legacy Zend interface.
      if (overview.value?.capabilities?.isLegacy) {
        legacyBillingDialogVisible.value = true;
        return;
      }
      buyCreditsDialogVisible.value = true;
    };
    const openAddPaymentMethodDialog = () => { addPaymentMethodDialogVisible.value = true; };

    const onUndoCancel = async () => {
      const result = await undoCancelPlan();
      await handleReload(result, { onFailure: (err) => showToastError(err) });
    };

    /**
     * Extend trial CTA handler (cf. UC 4.5).
     * Pre-condition `eligibleToExtendTrial` is checked by the backend; the
     * UI only ensures a payment card is present before triggering, since
     * the trial extension will switch the shop to a paying state after
     * +14 days and we need a way to charge them. Shopify shops bypass this
     * check (Shopify handles its own billing).
     */
    const onExtendTrial = async () => {
      if (!overview.value) return;

      const isShopify = overview.value.shop?.solutionEcommerce === 'shopify';
      const hasDefaultCard = (overview.value.paymentMethods?.items ?? []).some(
        (p) => p.isDefault,
      );

      if (!isShopify && !hasDefaultCard) {
        // Open the card-add modal first. The user will add a card via the
        // Adyen Drop-in, then we automatically chain the trial extension.
        pendingTrialExtendAfterCard.value = true;
        addPaymentMethodDialogVisible.value = true;
        return;
      }

      await doExtendTrial();
    };

    const doExtendTrial = async () => {
      const result = await extendTrial();
      await handleReload(result, {
        onFailure: (err) => showToastError(err),
      });
    };

    /**
     * When the user adds a card from the AddPaymentMethodDialog AND the
     * intent was to extend the trial, chain the extension on success.
     */
    const onCardAdded = async () => {
      if (pendingTrialExtendAfterCard.value) {
        pendingTrialExtendAfterCard.value = false;
        await doExtendTrial();
      }
    };

    const onUndoDowngrade = async () => {
      const result = await undoDowngradeNextPlan();
      await handleReload(result, { onFailure: (err) => showToastError(err) });
    };

    // ─── Payment method action confirmation flow ─────────────────────────
    // `set-default` and `delete` both prompt a small confirm dialog before
    // hitting the API — see BillingConfirmDialog at the bottom of the
    // template. `recentlyDefaultedId` drives the brand-colour pulse on the
    // card that just became default once /overview has refetched.
    type PaymentActionKind = 'set-default' | 'delete';

    const paymentActionConfirm = ref<{
      visible: boolean;
      action: PaymentActionKind | null;
      tokenId: string | null;
      title: string;
      message: string;
      confirmLabel: string;
      cancelLabel: string;
      variant: 'primary' | 'danger';
      submitting: boolean;
    }>({
      visible: false,
      action: null,
      tokenId: null,
      title: '',
      message: '',
      confirmLabel: '',
      cancelLabel: '',
      variant: 'primary',
      submitting: false,
    });

    const recentlyDefaultedId = ref<string | null>(null);

    const findCard = (id: string) => (
      overview.value?.paymentMethods?.items?.find((p) => p.id === id) ?? null
    );

    const openPaymentActionConfirm = (
      action: PaymentActionKind,
      id: string,
    ) => {
      const card = findCard(id);
      if (!card) {
        showToastError(t('billing.subscriptionPage.errors.actionFailed'));
        return;
      }
      const i18nKey = action === 'set-default'
        ? 'billing.subscriptionPage.paymentMethods.confirmSetDefault'
        : 'billing.subscriptionPage.paymentMethods.confirmDelete';
      paymentActionConfirm.value = {
        visible: true,
        action,
        tokenId: id,
        title: t(`${i18nKey}.title`),
        message: t(`${i18nKey}.message`, { brand: card.brand, last4: card.last4 }),
        confirmLabel: t(`${i18nKey}.confirm`),
        cancelLabel: t(`${i18nKey}.cancel`),
        variant: action === 'delete' ? 'danger' : 'primary',
        submitting: false,
      };
    };

    const onSetDefaultPaymentMethod = (id: string) => {
      openPaymentActionConfirm('set-default', id);
    };

    const onDeletePaymentMethod = (id: string) => {
      openPaymentActionConfirm('delete', id);
    };

    const onConfirmPaymentAction = async () => {
      const { action, tokenId } = paymentActionConfirm.value;
      if (!action || !tokenId) return;

      paymentActionConfirm.value.submitting = true;
      try {
        const result = action === 'set-default'
          ? await setDefaultPaymentMethod(tokenId)
          : await deletePaymentMethod(tokenId);

        if (!result) {
          showToastError(t('billing.subscriptionPage.errors.actionFailed'));
          return;
        }

        await store.dispatch('billing/refetchOverview');
        paymentActionConfirm.value.visible = false;

        // Set the pulse trigger *after* /overview has refetched so the
        // animation starts on the same render where the card has slid into
        // its new (top) position. The class is removed once the keyframe
        // duration has elapsed so future renders stay neutral.
        if (action === 'set-default') {
          recentlyDefaultedId.value = tokenId;
          setTimeout(() => {
            recentlyDefaultedId.value = null;
          }, 1500);
        }
      } finally {
        paymentActionConfirm.value.submitting = false;
      }
    };

    // BillsList emits raw shops_bills rows (SpmTableAPI convention). DataService
    // aliases columns as `${mainTable}.${field}` so the row keys are
    // `shops_bills.<col>` — we read via bracket access here.
    //
    // `products` ships in three shapes (Zend JSON array of strings, new-flow
    // JSON object of items with `.name`, or rarely a plain string) — we parse
    // them all so RepayInvoiceDialog never shows the raw JSON dump.
    const parseProductsLabel = (raw: any): string => {
      if (raw == null) return '';
      const str = String(raw).trim();
      if (!str) return '';
      try {
        const parsed = JSON.parse(str);
        const extract = (p: any): string => {
          if (typeof p === 'string') return p;
          if (p && typeof p === 'object') return p.name ?? p.label ?? p.title ?? '';
          return '';
        };
        let labels: string[] = [];
        if (Array.isArray(parsed)) {
          labels = parsed.map(extract).filter(Boolean);
        } else if (parsed && typeof parsed === 'object') {
          labels = Object.values(parsed).map(extract).filter(Boolean);
        }
        if (labels.length > 0) return labels.join(', ');
      } catch {
        /* not JSON — fall through */
      }
      return str;
    };

    const billRowToInvoiceListItem = (row: any): InvoiceListItem => {
      const peNum = Number(row?.['shops_bills.payment_error'] ?? -1);
      const status: InvoiceListItem['status'] =
        peNum === 0 ? 'paid' : peNum === 2 ? 'unpaid' : 'processing';
      const ht = Number(row?.['shops_bills.amount_ht'] ?? 0);
      const ttc = Number(row?.['shops_bills.amount_ttc'] ?? 0);
      const currency = overview.value?.shop?.currency === 'USD' ? 'USD' : 'EUR';
      const fmt = (n: number) => n.toLocaleString('fr-FR', {
        style: 'currency',
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
      return {
        id: String(row?.['shops_bills.id_ps_bundle'] ?? ''),
        date: String(row?.['shops_bills.date'] ?? ''),
        description: parseProductsLabel(row?.['shops_bills.products']),
        amountHt: { amount: ht, currency, label: fmt(ht) },
        amountTtc: { amount: ttc, currency, label: fmt(ttc) },
        status,
        canRepay: status === 'unpaid',
        canDownload: status === 'paid' || status === 'unpaid',
      };
    };

    const openPayInvoiceDialog = (row: any) => {
      if (!row) return;
      invoiceBeingRepaid.value = billRowToInvoiceListItem(row);
      repayInvoiceDialogVisible.value = true;
    };

    const onInvoiceRepaid = async () => {
      // La facture passe `unpaid` → `paid` via le webhook Adyen AUTHORISATION
      // (asynchrone, quelques secondes — même mécanique que onCreditsBought /
      // onPlanSubscribed). Un refresh immédiat unique courrait le webhook et
      // laisserait la facture affichée « impayée » jusqu'au prochain F5. On
      // recharge donc la table + l'overview sur une fenêtre de ~15s pour
      // absorber le délai du webhook.
      //
      // Pas de break anticipé : le statut de la facture vit dans la table
      // (SpmTableAPI), pas dans le store overview, donc aucun signal fiable à
      // observer ici — on parcourt tout le budget.
      const delays = [800, 1500, 2500, 4000, 6000];
      for (const delay of delays) {
        await new Promise((r) => { setTimeout(r, delay); });
        await Promise.all([
          billsListRef.value?.reloadData() ?? Promise.resolve(),
          store.dispatch('billing/refetchOverview'),
        ]);
      }
      invoiceBeingRepaid.value = null;
    };

    const downloadInvoice = async (row: any) => {
      // The PDF endpoint is JWT-guarded — a raw `window.open` would land on
      // NestJS without an Authorization header and 401. The helper fetches
      // the blob through the authenticated nestCall pipeline, then triggers
      // the download client-side.
      // Row keys are prefixed by DataService (see billRowToInvoiceListItem).
      const id = String(row?.['shops_bills.id_ps_bundle'] ?? '');
      if (!id) return;
      const ok = await downloadInvoicePdf(id);
      if (!ok) {
        showToastError(t('billing.subscriptionPage.errors.actionFailed'));
      }
    };

    const onAlertCta = async (alert: BillingAlert) => {
      const action = alert.cta?.action;
      if (action === 'open-change-plan') openChangePlanDialog();
      else if (action === 'undo-cancel') onUndoCancel();
      else if (action === 'shopify-bump-approve') {
        // The Shopify confirmation URL is only obtained as the response of the
        // bump call — it is not stored, so we fetch a fresh one on click.
        const result = await bumpShopifyCappedAmount();
        if (result?.confirmationUrl) {
          window.location.href = result.confirmationUrl;
        }
      }
    };

    onMounted(async () => {
      // BillsList (SpmTableAPI) loads its own data on mount — we only refresh
      // the overview here.
      await refreshOverview();
    });

    return {
      t,
      shopOffer,
      overview,
      loading,
      currencySymbol,
      isShopify,
      autoExclusionDialogVisible,
      openAutoExclusionDialog,
      isOnTrial,
      planDiscountPercent,
      visibleAlertsCount,
      billsListRef,
      unsubscribeDialogVisible,
      changePlanDialogVisible,
      migrateV5DialogVisible,
      addPaymentMethodDialogVisible,
      buyCreditsDialogVisible,
      repayInvoiceDialogVisible,
      invoiceBeingRepaid,
      legacyBillingDialogVisible,
      onInvoiceRepaid,
      onExtendTrial,
      onUndoDowngrade,
      onCardAdded,
      refreshOverview,
      openChangePlanDialog,
      openMigrateDialog,
      openUnsubscribeDialog,
      openBuyCreditsDialog,
      openAddPaymentMethodDialog,
      onUndoCancel,
      onSetDefaultPaymentMethod,
      onDeletePaymentMethod,
      paymentActionConfirm,
      onConfirmPaymentAction,
      onCreditsBought,
      onPlanSubscribed,
      recentlyDefaultedId,
      openPayInvoiceDialog,
      downloadInvoice,
      onAlertCta,
    };
  },
});
